import React, { useState } from 'react'

import { get, omit } from 'lodash'
import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Popover,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { PictureAsPdfRounded, BorderAllRounded } from '@mui/icons-material'
import { styled } from '@mui/styles'
import { useIntl } from 'react-intl'

import { AvoidCopy, ProgressBarLegend } from 'components'
import { BeheardReportUnlockButton, BeheardReportTableToCards } from '../../components'
import { fullReportItems } from 'constants/beheardReport'
import { downloadOpenEndedAnswersRequest } from 'core/requests'
import { SurveyDriverScore } from 'enums/survey'
import { t } from 'i18n'
import {
  getBeheardQuestionTrendIdentity,
  getDriverScoreDescription,
  getPreviousEffectivenessByQuestionIdentity,
} from 'utils/survey'

type LegendCircleProps = {
  color: string
}

const LegendCircle = styled('span')<LegendCircleProps>(({ color }) => ({
  height: '12px',
  width: '12px',
  marginRight: '8px',
  borderRadius: '100%',
  backgroundColor: color,
  display: 'inline-block',
}))

export const getSurveyReportQueryParams = filters => {
  const newFilters: any = { criteria: [] }

  Object.keys(omit(filters, 'roles')).forEach(key => {
    if (filters[key].length) {
      newFilters.criteria.push({
        category: +key,
        demographics: filters[key],
      })
    }
  })
  if (filters.roles) {
    newFilters.roles = filters.roles
  }

  return newFilters
}

export default function BehardReportFullReport(props: any) {
  const { surveyDetails, /*isFullReport,*/ surveyPermissionId } = props
  const dontShowVisual = get(surveyDetails, "previous_survey_percents") ? true : false;
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const intl = useIntl()

  const openEndedDownloadButtons = [
    {
      label: intl.formatMessage({
        id: 'beheardReport.fullReport.buttonLabels.xlsx',
      }),
      Icon: BorderAllRounded,
      format: 'xlsx',
    },
    {
      label: intl.formatMessage({
        id: 'beheardReport.fullReport.buttonLabels.pdf',
      }),
      Icon: PictureAsPdfRounded,
      format: 'pdf',
    },
  ]

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [openedPopoverId, setOpenedPopoverId] = useState<number | null>(null)

  const handleClick =
    (questionId: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
      setOpenedPopoverId(questionId)
    }

  const handleClose = () => {
    setAnchorEl(null)
    setOpenedPopoverId(null)
  }

  const drivers = get(surveyDetails, 'scores.percents.drivers')

  const findEngagementIndexForDriver = (driver: any) =>
    (surveyDetails.engagement_index ?? []).find((index: any) =>
      index.driver?.key
        ? index.driver.key === driver.key
        : index.driver?.label === driver.label,
    )

  const hasRecommendations = (question, type) =>
    surveyDetails.top_five &&
    surveyDetails.top_five.find(
      topFive => topFive.question_id === question.id && topFive.type === type,
    )

  const previousSatisfactions = getPreviousEffectivenessByQuestionIdentity(
    surveyDetails['previous_engagement_index'],
  )

  const renderTableContent = (driver: any) => {
    const engagementIndex = findEngagementIndexForDriver(driver)

    if (!engagementIndex || engagementIndex.questions.length === 0) {
      return null
    }

    const columns = [
      {
        key: 'statement',
        label: intl.formatMessage({
          id: 'beheardReport.fullReport.tableHeaders.score',
        }),
        align: 'left' as const,
        isStatement: true,
      },
      {
        key: 'effectiveness',
        label: intl.formatMessage({
          id: 'beheardReport.fullReport.tableHeaders.effectiveness',
        }),
        align: 'center' as const,
      },
      ...(dontShowVisual ? [{
        key: 'previous_effectiveness',
        label: intl.formatMessage({
          id: 'beheardReport.fullReport.tableHeaders.previousEffectiveness',
        }),
        align: 'center' as const,
      }] : []),
      {
        key: 'importance',
        label: intl.formatMessage({
          id: 'beheardReport.fullReport.tableHeaders.importance',
        }),
        align: 'center' as const,
      },
      {
        key: 'benchmark',
        label: intl.formatMessage({
          id: 'beheardReport.fullReport.tableHeaders.benchmark',
        }),
        align: 'center' as const,
      },
    ]

    const data = engagementIndex.questions.map((question: any) => {
      const previousEffectiveness =
        previousSatisfactions[
          getBeheardQuestionTrendIdentity(question, engagementIndex.driver?.key)
        ]

      return {
        statement: (
          <Box>
            <Grid container component="span" spacing={1} alignItems="center">
              <Grid component="span" item>
                <Box display="flex" alignItems="center">
                  {hasRecommendations(question, 'strength') && (
                    <LegendCircle color={theme.palette.primary.main} />
                  )}
                  {hasRecommendations(question, 'action') && (
                    <LegendCircle color={theme.palette.secondary.main} />
                  )}
                </Box>
              </Grid>
              <Grid component="span" item xs sx={{ fontSize: 16 }}>
                <AvoidCopy>{question.statement}</AvoidCopy>
              </Grid>
            </Grid>
            {hasRecommendations(question, 'action') && (
              <Box mt={1}>
                <Link
                  component="button"
                  color="secondary"
                  style={{
                    marginTop: '8px',
                    padding: 0,
                    textAlign: 'left',
                    display: 'block',
                    width: '100%'
                  }}
                >
                  <Typography
                    variant="body2"
                    color="secondary.main"
                    fontWeight="bold"
                    onClick={handleClick(question.id)}
                  >
                    {t('beheardReport.fullReport.tableData.viewRecommendations')}
                  </Typography>
                </Link>
                <Popover
                  id={question.id}
                  elevation={2}
                  open={openedPopoverId === question.id}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    style: {
                      maxWidth: '90vw',
                      width: 'auto',
                      minWidth: 280,
                      marginTop: 8,
                      marginLeft: isMobile ? 16 : 0,
                      marginRight: isMobile ? 16 : 0,
                    },
                  }}
                >
                  <Box
                    m={2}
                    pl={2}
                    style={{
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      maxHeight: '80vh',
                      overflowY: 'auto',
                    }}
                  >
                    {hasRecommendations(question, 'action').recommendations.map(
                      (recommendation: string, index: number) => (
                        <Typography
                          key={recommendation + index}
                          variant="body2"
                          color="text.light"
                          paragraph
                        >
                          {recommendation}
                        </Typography>
                      )
                    )}
                  </Box>
                </Popover>
              </Box>
            )}
          </Box>
        ),
        effectiveness: (
          <Typography
            variant="body2"
            fontWeight="bold"
            color={
              question.percentages.effectiveness <= 69
                ? 'error'
                : question.percentages.effectiveness > 79
                ? 'primary'
                : 'secondary'
            }
            align="center"
          >
            {question.percentages.effectiveness !== null
              ? `${question.percentages.effectiveness}%`
              : '--'}
          </Typography>
        ),
        previous_effectiveness: dontShowVisual ? (
          <Typography
            variant="body2"
            fontWeight="bold"
            color={
              previousEffectiveness === null ||
              previousEffectiveness === undefined ||
              previousEffectiveness <= 69
                ? 'error'
                : previousEffectiveness > 79
                ? 'primary'
                : 'secondary'
            }
            align="center"
          >
            {previousEffectiveness !== null && previousEffectiveness !== undefined
              ? `${previousEffectiveness}%`
              : '--'}
          </Typography>
        ) : null,
        importance: (
          <Typography variant="body2" fontWeight="bold" align="center">
            {question.percentages.importance !== null
              ? `${question.percentages.importance}%`
              : '--'}
          </Typography>
        ),
        benchmark: (
          <Typography variant="body2" fontWeight="bold" align="center">
            {/* {(previousSatisfactions && previousSatisfactions[question.id]
              ? `${previousSatisfactions[question.id]}%`
              : 'N/A')} */}
            {question.percentages.benchmark !== null
              ? `${question.percentages.benchmark}%`
              : '--'}
          </Typography>
        ),
      }
    })

    return (
      <BeheardReportTableToCards
        columns={columns}
        data={data}
        title={driver.label}
      />
    )
  }

  return (
    <Grid container>
      {get(surveyDetails, 'sections_allowed.full_report') &&
        surveyDetails.survey &&
        !!drivers && (
          <>
            {isMobile ? (
              <Grid container>
                {[
                  {
                    label: intl.formatMessage({
                      id: 'beheardReport.fullReport.engagementScore',
                    }),
                    value: drivers?.find(
                      engagement => engagement.key === SurveyDriverScore.Engagement,
                    )?.value,
                  },
                  {
                    label: intl.formatMessage({
                      id: 'beheardReport.fullReport.participationRate',
                    }),
                    value: surveyDetails?.survey?.participation_rate,
                  },
                ].map(headerItem => (
                  <Grid item xs={12} sm={6} key={headerItem.label}>
                    <Typography
                      variant='h1'
                      style={{ fontSize: 80 }}
                      color='text.primary'
                      fontWeight={600}
                      align='center'
                    >
                      {Math.round(headerItem.value)}%
                    </Typography>

                    <Typography
                      variant='h5'
                      style={{ fontSize: 16, textTransform: 'uppercase' }}
                      color='text.primary'
                      paragraph
                      align='center'
                    >
                      {headerItem.label}
                    </Typography>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Box py={2} width={1}>
                    <Divider />
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Grid container>
                {[
                  {
                    label: intl.formatMessage({
                      id: 'beheardReport.fullReport.engagementScore',
                    }),
                    value: drivers?.find(
                      engagement => engagement.key === SurveyDriverScore.Engagement,
                    )?.value,
                  },
                  {
                    label: intl.formatMessage({
                      id: 'beheardReport.fullReport.participationRate',
                    }),
                    value: surveyDetails?.survey?.participation_rate,
                  },
                ].map(headerItem => (
                  <Grid item xs={12} sm={6} key={headerItem.label}>
                    <Typography
                      variant='h1'
                      style={{ fontSize: 80 }}
                      color='text.primary'
                      fontWeight={600}
                      align='center'
                    >
                      {Math.round(headerItem.value)}%
                    </Typography>

                    <Typography
                      variant='h5'
                      style={{ fontSize: 16, textTransform: 'uppercase' }}
                      color='text.primary'
                      paragraph
                      align='center'
                    >
                      {headerItem.label}
                    </Typography>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Box py={2} width={1}>
                    <Divider />
                  </Box>
                </Grid>
              </Grid>
            )}
          </>
        )}

      {drivers && (
        <Grid container>
          {drivers.map(driver => {
            const engagementIndexForDriver = findEngagementIndexForDriver(driver)

            return (
              ((driver.key !== SurveyDriverScore.CustomRatingsQuestions &&
                get(surveyDetails, 'sections_allowed.full_report')) ||
                (driver.key === SurveyDriverScore.CustomRatingsQuestions &&
                  get(surveyDetails, 'sections_allowed.custom_ratings'))) && (
                <Box
                  my={2}
                  width={1}
                  key={driver.key}
                  id={fullReportItems[driver.key]}
                >
                  <Grid item xs={12}>
                    <Typography
                      variant='h5'
                      color='text.primary'
                      fontWeight={600}
                    >
                      {driver.label}
                    </Typography>

                    <Typography variant='body2' color='text.primary' sx={{ fontSize: 16 }}>
                      {getDriverScoreDescription(driver.key)}
                    </Typography>
                  </Grid>

                  {engagementIndexForDriver ? (
                    <>
                      {engagementIndexForDriver.questions.length > 0 ? (
                        <>
                          <Grid item xs={12}>
                            <Box>
                              <Grid container alignItems='center'>
                                <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                                  {[
                                    {
                                      label: intl.formatMessage({
                                        id: 'beheardReport.fullReport.legend.strengths',
                                      }),
                                      color: 'primary',
                                    },
                                    {
                                      label: intl.formatMessage({
                                        id: 'beheardReport.fullReport.legend.recommendedActionAreas',
                                      }),
                                      color: 'secondary',
                                    },
                                  ].map(legendSection => (
                                    <Typography
                                      key={legendSection.label}
                                      variant='body2'
                                      color='text.primary'
                                      paragraph
                                      sx={{ fontSize: 16 }}
                                    >
                                      <LegendCircle
                                        color={
                                          theme.palette[legendSection.color]
                                            .main
                                        }
                                      />
                                      {legendSection.label}
                                    </Typography>
                                  ))}
                                </Grid>

                                <Grid item xs={12} sm={12} lg={10}>
                                  <ProgressBarLegend />
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            {renderTableContent(driver)}
                          </Grid>
                        </>
                      ) : (
                        driver.key ===
                          SurveyDriverScore.CustomRatingsQuestions && (
                          <Typography
                            variant='body2'
                            color='text.primary'
                            fontWeight={500}
                          >
                            {t(
                              'beheardReport.fullReport.noCustomRatingQuestions',
                            )}
                          </Typography>
                        )
                      )}
                    </>
                  ) : (
                    <BeheardReportUnlockButton {...props} />
                  )}
                  <Box mb={2}>
                    <Divider
                      style={{
                        height:
                          driver.key ===
                          SurveyDriverScore.CustomRatingsQuestions
                            ? 0
                            : 3,
                      }}
                    />
                  </Box>
                </Box>
              )
            )
          })}

          {get(
            surveyDetails,
            'sections_allowed.custom_open_ended_download',
          ) && (
            <Box my={2} width={1} key={'open-ended-questions'}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography
                    variant='h5'
                    color='text.primary'
                    fontWeight={600}
                    paragraph
                  >
                    {t('beheardReport.fullReport.openEndedQuestions')}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  {surveyDetails.has_open_ended_answers ? (
                    <Grid container spacing={1}>
                      {openEndedDownloadButtons.map(downloadButton => (
                        <Grid item key={downloadButton.label}>
                          <Button
                            style={{ minWidth: 300 }}
                            variant='outlined'
                            color='primary'
                            startIcon={<downloadButton.Icon />}
                            onClick={() =>
                              downloadOpenEndedAnswersRequest(
                                surveyDetails.survey.id,
                                surveyPermissionId,
                                downloadButton.format,
                                getSurveyReportQueryParams(props?.filtersState),
                              )
                            }
                          >
                            {downloadButton.label}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography
                      variant='body2'
                      color='text.primary'
                      fontWeight={500}
                    >
                      {t('beheardReport.fullReport.noCustomOpenEndedQuestions')}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>
      )}
    </Grid>
  )
}
